import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { downloadCSV, downloadPDF } from '../utils/exportUtils';
import { formatDateUK } from '../utils/dateUtils';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Server-side DataTable component for Member Calendar page
 * Matches Laravel TicketOfMemberDataTable.js with all 18 columns
 */

// Laravel ticket status IDs (from config/tickets.php)
const TICKET_STATUS = {
  IN_ACTION: 2,
  REQUEST: 4,
  COMPLETED: 5,
  DECLINED: 6,
  CONTRACTOR_COMPLETED: 7,
  BUILDING_SET_UP: 8,
};

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const MemberTicketsDataTable = ({ userId, statusFilter, contractors, onTicketClick }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // Server-side pagination state
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30); // Laravel default
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredRecords, setFilteredRecords] = useState(0);
  const [draw, setDraw] = useState(1);
  const [search, setSearch] = useState("");

  // Column visibility - matching Laravel hidden columns by default
  const [visibleColumns, setVisibleColumns] = useState([
    "id",
    "status",
    "urgency",
    // "property_id", // Hidden by default
    "property_name",
    "postcode",
    "created_at",
    "book_in_date",
    "date_estimated_completion",
    "date_completed",
    "job_type",
    "client_view",
    "guardian_view",
    // "contact_first_name", // Hidden by default
    // "contact_last_name", // Hidden by default
    "contractor",
    // "cost_of_works", // Hidden by default
    "actions"
  ]);
  const [showColDropdown, setShowColDropdown] = useState(false);

  // Column mapping for server-side sorting (matches backend sortColumns array)
  const getSortColumnIndex = (field) => {
    const mapping = {
      "id": 0,
      "status": 1,
      "urgency": 2,
      "property_id": 3,
      "property_name": 4,
      "postcode": 5,
      "created_at": 6,
      "book_in_date": 7,
      "date_estimated_completion": 8,
      "date_completed": 9,
      "job_type": 10,
      "client_view": 11,
      "guardian_view": 12,
      "contact_first_name": 13,
      "contact_last_name": 14,
      "contractor": 15,
      "cost_of_works": 16,
      "actions": 17
    };
    return mapping[field] || 0;
  };

  // Load data from server
  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const requestData = {
        draw: draw,
        start: (currentPage - 1) * pageSize,
        length: pageSize === -1 ? 9999999 : pageSize,
        search: { value: search },
        order: [{ column: getSortColumnIndex(sortField), dir: sortDirection }],
        status_id: statusFilter || null
      };

      console.log(`📊 [DATATABLE] Loading data: page=${currentPage}, size=${pageSize}, status=${statusFilter}`);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/member-dt/${userId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(requestData)
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      console.log(`📊 [DATATABLE] Received ${result.data?.length || 0} records (total: ${result.recordsTotal}, filtered: ${result.recordsFiltered})`);

      setData(result.data || []);
      setTotalRecords(result.recordsTotal || 0);
      setFilteredRecords(result.recordsFiltered || 0);
      setDraw(result.draw);

    } catch (error) {
      console.error('❌ [DATATABLE] Error loading tickets:', error);
      setData([]);
      setTotalRecords(0);
      setFilteredRecords(0);
    } finally {
      setLoading(false);
    }
  }, [userId, currentPage, pageSize, search, sortField, sortDirection, statusFilter, draw]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset to page 1 when status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  // Handle search with debounce
  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1); // Reset to first page
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle checkbox toggle (client_view/guardian_view)
  const handleCheckboxToggle = async (ticketId, field, currentValue) => {
    try {
      const endpoint = field === 'client_view' ? 'toggle-client-view' : 'toggle-guardian-view';
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/${ticketId}/${endpoint}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            [field]: currentValue ? 0 : 1
          })
        }
      );

      if (!response.ok) throw new Error(`Failed to update ${field}`);

      // Reload data
      await loadData();

    } catch (error) {
      console.error(`Error toggling ${field}:`, error);
      alert(`Failed to update ${field}`);
    }
  };

  // Handle delete ticket - matches Laravel behavior
  const handleDeleteTicket = async (ticketId) => {
    // Laravel exact confirmation message
    if (!window.confirm('Are you sure you want to remove?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/${ticketId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to delete ticket');

      const result = await response.json();

      if (result.success) {
        alert(result.message || 'Ticket deleted successfully!');
        // Reload data
        await loadData();
      } else {
        alert(result.message || 'Failed to delete ticket');
      }

    } catch (error) {
      console.error('Error deleting ticket:', error);
      alert('Failed to delete ticket. Please try again.');
    }
  };

  // Handle accept ticket - Laravel contractor action
  const handleAcceptTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to accept the ticket?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/${ticketId}/contractors/accept`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to accept ticket');

      const result = await response.json();

      if (result.success) {
        alert(result.message || 'Ticket accepted successfully!');
        await loadData();
      } else {
        alert(result.message || 'Failed to accept ticket');
      }

    } catch (error) {
      console.error('Error accepting ticket:', error);
      alert('Failed to accept ticket. Please try again.');
    }
  };

  // Handle decline ticket - Laravel contractor action
  const handleDeclineTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to decline the ticket?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/${ticketId}/contractors/decline`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to decline ticket');

      const result = await response.json();

      if (result.success) {
        alert(result.message || 'Ticket declined successfully!');
        await loadData();
      } else {
        alert(result.message || 'Failed to decline ticket');
      }

    } catch (error) {
      console.error('Error declining ticket:', error);
      alert('Failed to decline ticket. Please try again.');
    }
  };

  // Handle complete ticket - Laravel contractor action
  const handleCompleteTicket = async (ticketId) => {
    if (!window.confirm('Are you sure?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/${ticketId}/contractors/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to complete ticket');

      const result = await response.json();

      if (result.success) {
        alert(result.message || 'Ticket completed successfully!');
        await loadData();
      } else {
        alert(result.message || 'Failed to complete ticket');
      }

    } catch (error) {
      console.error('Error completing ticket:', error);
      alert('Failed to complete ticket. Please try again.');
    }
  };

  // Check if ticket is overdue - Laravel behavior
  const isTicketOverdue = (row) => {
    // Only check for "In Action" status tickets
    if (row.statusId !== TICKET_STATUS.IN_ACTION) {
      return false;
    }

    // Check if deadline exists and has passed
    if (!row.date_estimated_completion) {
      return false;
    }

    const deadline = new Date(row.date_estimated_completion);
    const now = new Date();

    return deadline < now;
  };

  // Export functions
  const handleExportCSV = async () => {
    try {
      // Get all data for export (no pagination)
      const requestData = {
        draw: 1,
        start: 0,
        length: 9999999,
        search: { value: search },
        order: [{ column: getSortColumnIndex(sortField), dir: sortDirection }],
        status_id: statusFilter || null
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/member-dt/${userId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(requestData)
        }
      );

      if (!response.ok) throw new Error('Failed to fetch data for export');

      const result = await response.json();
      const exportData = result.data || [];

      // Transform data for CSV (only exportable columns)
      const csvData = exportData.map(row => ({
        'ID': row.ticketId,
        'Status': row.status,
        'Urgency': row.urgency,
        'Property ID': row.property_id,
        'Property Name': row.property_name,
        'Postcode': row.postcode,
        'Submitted': formatDateUK(row.created_at),
        'Book In': formatDateUK(row.book_in_date),
        'Deadline': formatDateUK(row.date_estimated_completion),
        'Job Type': row.job_type,
        'C. First': row.contact_first_name,
        'C. Last': row.contact_last_name,
        'Contractor': row.contractor,
        'Cost of Works': row.cost_of_works
      }));

      downloadCSV(csvData, `member-tickets-${userId}`);

    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV');
    }
  };

  const handleExportPDF = async () => {
    try {
      // Get all data for export
      const requestData = {
        draw: 1,
        start: 0,
        length: 9999999,
        search: { value: search },
        order: [{ column: getSortColumnIndex(sortField), dir: sortDirection }],
        status_id: statusFilter || null
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/member-dt/${userId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(requestData)
        }
      );

      if (!response.ok) throw new Error('Failed to fetch data for export');

      const result = await response.json();
      const exportData = result.data || [];

      // Transform data for PDF
      const pdfData = exportData.map(row => ({
        'ID': row.ticketId,
        'Status': row.status,
        'Urgency': row.urgency,
        'Property': row.property_name,
        'Postcode': row.postcode,
        'Submitted': formatDateUK(row.created_at),
        'Book In': formatDateUK(row.book_in_date),
        'Contractor': row.contractor
      }));

      downloadPDF(pdfData, `member-tickets-${userId}`, 'Member Tickets', 'landscape');

    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    if (visibleColumns.includes(columnKey)) {
      setVisibleColumns(visibleColumns.filter(col => col !== columnKey));
    } else {
      setVisibleColumns([...visibleColumns, columnKey]);
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredRecords / (pageSize === -1 ? filteredRecords : pageSize));
  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, filteredRecords);

  // Render sort icon
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <SortIcon direction={null} isActive={false} />;
    }
    return <SortIcon direction={sortDirection} isActive={true} />;
  };

  return (
    <div className="datatable-wrapper">
      <input type="hidden" name="clickedStatusId" value={statusFilter || ''} />

      {/* Toolbar */}
      <div className="dt-toolbar" style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label style={{ marginBottom: 0 }}>
            Show{' '}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              className="form-control"
              style={{ display: 'inline-block', width: 'auto', fontSize: '12px', padding: '3px 8px' }}
            >
              <option value="30">30</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="75">75</option>
              <option value="100">100</option>
              <option value="-1">All</option>
            </select>{' '}
            entries
          </label>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Export buttons */}
          <button className="btn btn-sm btn-secondary" onClick={handleExportCSV} title="Export as CSV">
            <i className="fa fa-file-excel-o"></i> CSV
          </button>
          <button className="btn btn-sm btn-secondary" onClick={handleExportPDF} title="Export as PDF">
            <i className="fa fa-file-pdf-o"></i> PDF
          </button>
          <button className="btn btn-sm btn-secondary hidden-print" onClick={handlePrint} title="Print table">
            <i className="fa fa-print"></i> Print
          </button>

          {/* Column visibility toggle */}
          <div className="dropdown" style={{ position: 'relative', display: 'inline-block' }}>
            <button
              className="btn btn-sm btn-secondary dropdown-toggle"
              type="button"
              onClick={() => setShowColDropdown(!showColDropdown)}
            >
              Columns
            </button>
            {showColDropdown && (
              <>
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999
                  }}
                  onClick={() => setShowColDropdown(false)}
                />
                <div style={{
                  position: 'absolute',
                  right: 0,
                  top: '100%',
                  zIndex: 1000,
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '12px',
                  minWidth: '180px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  marginTop: '4px',
                  color: '#333'
                }}>
                  <div style={{ marginBottom: '8px', cursor: 'pointer', fontSize: '14px', color: '#333' }} onClick={() => toggleColumn('property_id')}>
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes('property_id')}
                      onChange={() => toggleColumn('property_id')}
                      style={{ marginRight: '8px' }}
                    />
                    <span>Property ID</span>
                  </div>
                  <div style={{ marginBottom: '8px', cursor: 'pointer', fontSize: '14px', color: '#333' }} onClick={() => toggleColumn('contact_first_name')}>
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes('contact_first_name')}
                      onChange={() => toggleColumn('contact_first_name')}
                      style={{ marginRight: '8px' }}
                    />
                    <span>C. First</span>
                  </div>
                  <div style={{ marginBottom: '8px', cursor: 'pointer', fontSize: '14px', color: '#333' }} onClick={() => toggleColumn('contact_last_name')}>
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes('contact_last_name')}
                      onChange={() => toggleColumn('contact_last_name')}
                      style={{ marginRight: '8px' }}
                    />
                    <span>C. Last</span>
                  </div>
                  <div style={{ marginBottom: '0', cursor: 'pointer', fontSize: '14px', color: '#333' }} onClick={() => toggleColumn('cost_of_works')}>
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes('cost_of_works')}
                      onChange={() => toggleColumn('cost_of_works')}
                      style={{ marginRight: '8px' }}
                    />
                    <span>Cost of Works</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Search */}
          <label style={{ marginBottom: 0 }}>
            Search:{' '}
            <input
              type="text"
              className="form-control"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ display: 'inline-block', width: 'auto', fontSize: '12px', padding: '3px 8px' }}
              placeholder="Search tickets..."
            />
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover dt-responsive js-tickets-dt" style={{ fontSize: '11px', width: '100%' }}>
          <thead>
            <tr>
              <th onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}>
                ID{renderSortIcon('id')}
              </th>
              <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                Status{renderSortIcon('status')}
              </th>
              <th onClick={() => handleSort('urgency')} style={{ cursor: 'pointer' }}>
                Urgency{renderSortIcon('urgency')}
              </th>
              {visibleColumns.includes('property_id') && (
                <th onClick={() => handleSort('property_id')} style={{ cursor: 'pointer' }}>
                  Property ID{renderSortIcon('property_id')}
                </th>
              )}
              <th onClick={() => handleSort('property_name')} style={{ cursor: 'pointer' }}>
                Property Name{renderSortIcon('property_name')}
              </th>
              <th onClick={() => handleSort('postcode')} style={{ cursor: 'pointer' }}>
                Postcode{renderSortIcon('postcode')}
              </th>
              <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }} className="js-tooltip" title="Date when submitted">
                Submitted{renderSortIcon('created_at')}
              </th>
              <th onClick={() => handleSort('book_in_date')} style={{ cursor: 'pointer' }} className="js-tooltip" title="Book in date">
                Book in{renderSortIcon('book_in_date')}
              </th>
              <th onClick={() => handleSort('date_estimated_completion')} style={{ cursor: 'pointer' }} className="js-tooltip" title="Proposed deadline">
                Deadline{renderSortIcon('date_estimated_completion')}
              </th>
              <th onClick={() => handleSort('date_completed')} style={{ cursor: 'pointer' }} className="js-tooltip" title="Date when completed">
                Completed{renderSortIcon('date_completed')}
              </th>
              <th onClick={() => handleSort('job_type')} style={{ cursor: 'pointer' }}>
                Job type{renderSortIcon('job_type')}
              </th>
              <th className="dt-body-center" style={{ textAlign: 'center' }}>Client view</th>
              <th className="dt-body-center" style={{ textAlign: 'center' }}>Guardian view</th>
              {visibleColumns.includes('contact_first_name') && (
                <th onClick={() => handleSort('contact_first_name')} style={{ cursor: 'pointer' }} className="js-tooltip" title="Contractor first name">
                  C. First{renderSortIcon('contact_first_name')}
                </th>
              )}
              {visibleColumns.includes('contact_last_name') && (
                <th onClick={() => handleSort('contact_last_name')} style={{ cursor: 'pointer' }} className="js-tooltip" title="Contractor last name">
                  C. Last{renderSortIcon('contact_last_name')}
                </th>
              )}
              <th onClick={() => handleSort('contractor')} style={{ cursor: 'pointer' }}>
                Contractor{renderSortIcon('contractor')}
              </th>
              {visibleColumns.includes('cost_of_works') && (
                <th onClick={() => handleSort('cost_of_works')} style={{ cursor: 'pointer' }}>
                  Cost of works{renderSortIcon('cost_of_works')}
                </th>
              )}
              <th className="hidden-print">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="18" style={{ textAlign: 'center', padding: '30px' }}>
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan="18" style={{ textAlign: 'center', padding: '30px', color: '#999' }}>
                  No tickets!
                </td>
              </tr>
            ) : (
              data.map((row) => (
                <tr
                  key={row.ticketId}
                  className={isTicketOverdue(row) ? 'row-ticket-danger' : ''}
                  style={isTicketOverdue(row) ? {
                    backgroundColor: '#dc3545',
                    color: '#fff'
                  } : {}}
                >
                  <td>{row.ticketId}</td>
                  <td>{row.status || '-'}</td>
                  <td>{row.urgency || '-'}</td>
                  {visibleColumns.includes('property_id') && <td>{row.property_id || '-'}</td>}
                  <td>{row.property_name || '-'}</td>
                  <td>{row.postcode || '-'}</td>
                  <td>{formatDateUK(row.created_at) || '-'}</td>
                  <td>{formatDateUK(row.book_in_date) || '-'}</td>
                  <td>{formatDateUK(row.date_estimated_completion) || '-'}</td>
                  <td>{formatDateUK(row.date_completed) || '-'}</td>
                  <td>{row.job_type || '-'}</td>
                  <td className="dt-body-center tiny-column" style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={row.client_view && row.client_view[0] === 1}
                      onChange={() => handleCheckboxToggle(row.client_view[1], 'client_view', row.client_view[0])}
                      id={`client_view-${row.client_view[1]}`}
                      data-ticket-id={row.client_view[1]}
                    />
                  </td>
                  <td className="dt-body-center tiny-column" style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={row.guardian_view && row.guardian_view[0] === 1}
                      onChange={() => handleCheckboxToggle(row.guardian_view[1], 'guardian_view', row.guardian_view[0])}
                      id={`guardian_view-${row.guardian_view[1]}`}
                      data-ticket-id={row.guardian_view[1]}
                    />
                  </td>
                  {visibleColumns.includes('contact_first_name') && <td>{row.contact_first_name || '-'}</td>}
                  {visibleColumns.includes('contact_last_name') && <td>{row.contact_last_name || '-'}</td>}
                  <td>{row.contractor || '-'}</td>
                  {visibleColumns.includes('cost_of_works') && <td>{row.cost_of_works || '-'}</td>}
                  <td className="action hidden-print">
                    {/* View button - always visible */}
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => onTicketClick && onTicketClick(row.ticketId)}
                      style={{ marginRight: '5px' }}
                      title="View Ticket"
                    >
                      <i className="fa fa-eye"></i>
                    </button>

                    {/* Edit button - with permission check */}
                    {hasPermission('tickets.edit') && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => navigate(`/tickets/${row.ticketId}/edit`)}
                        style={{ marginRight: '5px' }}
                        title="Edit Ticket"
                      >
                        <i className="fa fa-edit"></i>
                      </button>
                    )}

                    {/* Delete button - with permission check */}
                    {hasPermission('tickets.delete') && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteTicket(row.ticketId)}
                        style={{ marginRight: '5px' }}
                        title="Delete Ticket"
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    )}

                    {/* Contractor action buttons - with permission check */}
                    {hasPermission('tickets.contractors.manage') && (
                      <>
                        {/* Accept/Decline buttons - only for Request status */}
                        {row.statusId === TICKET_STATUS.REQUEST && (
                          <>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleAcceptTicket(row.ticketId)}
                              style={{ marginRight: '5px' }}
                              title="Accept Ticket"
                            >
                              Accept
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeclineTicket(row.ticketId)}
                              style={{ marginRight: '5px' }}
                              title="Decline Ticket"
                            >
                              Decline
                            </button>
                          </>
                        )}

                        {/* Complete button - for non-completed tickets */}
                        {row.statusId !== TICKET_STATUS.COMPLETED && (
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => handleCompleteTicket(row.ticketId)}
                            style={{ backgroundColor: '#2e8b57', border: 'none' }}
                            title="Complete Ticket"
                          >
                            Complete
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer with pagination */}
      <div className="row" style={{ marginTop: '15px' }}>
        <div className="col-sm-5">
          <div className="dataTables_info" style={{ paddingTop: '8px' }}>
            {filteredRecords === 0 ? (
              'Showing 0 to 0 of 0 entries'
            ) : (
              <>
                Showing {startRecord} to {endRecord} of {filteredRecords} entries
                {filteredRecords !== totalRecords && ` (filtered from ${totalRecords} total entries)`}
              </>
            )}
          </div>
        </div>
        <div className="col-sm-7">
          <div className="dataTables_paginate paging_simple_numbers" style={{ float: 'right' }}>
            <ul className="pagination" style={{ margin: 0 }}>
              <li className={`paginate_button page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>

              {pageSize !== -1 && [...Array(totalPages)].map((_, index) => {
                const pageNum = index + 1;
                // Show first, last, and pages around current
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                ) {
                  return (
                    <li key={index} className={`paginate_button page-item ${currentPage === pageNum ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  );
                } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                  return <li key={index} className="paginate_button page-item disabled"><span className="page-link">...</span></li>;
                }
                return null;
              })}

              <li className={`paginate_button page-item next ${currentPage >= totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberTicketsDataTable;
